import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useSetLang, type SupportedLang } from '@/hooks/useLangSync';

/**
 * LangToggle — D-12 / LOC-03 / D-03 (Plan 03-01 polish).
 *
 * Phase 2 contract preserved:
 *  - Text-only MK/EN button (NO flag icons).
 *  - Click flips via useSetLang() which uses history.replaceState (D-17).
 *  - aria-label gives screen readers the destination language.
 *  - Focus ring: 2px orange offset.
 *
 * Phase 3 (this plan) addition:
 *  - The label span is wrapped in <AnimatePresence mode="wait"> with a
 *    motion.span keyed to `current` so the label crossfades on toggle.
 *    This is the only motion/react use in Plan 03-01 (D-03 lang-toggle crossfade).
 *  - useReducedMotion() short-circuits the crossfade for users who prefer
 *    reduced motion (D-04 / A11Y-04 — sets transition.duration to 0).
 */
export function LangToggle(): JSX.Element {
  const { t, i18n } = useTranslation();
  const setLang = useSetLang();
  const reduce = useReducedMotion() ?? false;

  const current: SupportedLang = i18n.language === 'en' ? 'en' : 'mk';
  const next: SupportedLang = current === 'mk' ? 'en' : 'mk';
  const label = t('nav.langToggleLabel');

  return (
    <button
      type="button"
      onClick={() => setLang(next)}
      aria-label={`Switch language to ${next.toUpperCase()}`}
      className="text-mono-md text-ink hover:bg-ink hover:text-paper focus-visible:ring-orange rounded-pill px-3 py-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={current}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: reduce ? 0 : 0.15 }}
          className="inline-block"
        >
          {label}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}

export default LangToggle;
